import React, { ReactElement } from 'react';
import AvTable from '@components/Table/AvTable';
import { AvTableProps, HeadCellType, SortType } from '@fabric-ui/components/Table';
import { flex } from '@fabric-ui/theme';
import { Box, tableCellClasses, tableHeadClasses, tableRowClasses } from '@mui/material';
import { Field, TableCategoryWidgetTypes } from '../../../types';
import { getTableTotalProps } from './utils';

export interface Props {
  widget: TableCategoryWidgetTypes;
  commonTableProps: {
    enableSelectionColor: boolean;
    hasHeaderFilters: boolean;
    allowEdit: boolean;
    disableUseEffect: boolean;
  };
  data: any[];
  headCells: HeadCellType[];
  isLoading: boolean;
  isLoadingDrillDown: boolean;
  crossTableView: boolean | undefined;
  onSort: (field: string, direction: SortType | null) => void;
  activeRow: string[];
  nextDrillDownField: Field;
  onSelect: ((...v: any[]) => void) | undefined;
  onActiveCross: (v: any) => void;
  showExpandedRow: boolean | undefined;
  rowLimit: number;
  expandedRow: (row: any, onChange: any, isItemSelected: any) => ReactElement;
  setOffset: (offset: number) => void;
  totalRowCount: number | undefined;
  totalRowCountDrillDown: number | undefined;
  responseTotals: Record<string, any> | undefined;
  visibleHeadCells: HeadCellType[];
}

const createHash = ({ requests, id }: TableCategoryWidgetTypes) => JSON.stringify([requests, id]);

const LocalPaginationTable = (props: Props) => {
  const { widget, data, crossTableView, totalRowCount, totalRowCountDrillDown } = props;
  const totalRowsCount = crossTableView ? totalRowCount : totalRowCountDrillDown || totalRowCount;
  const [page, setPage] = React.useState(0);
  const limit = widget.definition.custom.topN || Infinity;
  const pageSize = widget.definition.custom.pageSize || 25;
  const pageData = React.useMemo(() => data.slice(page * pageSize, page * pageSize + pageSize), [data, pageSize, page]);
  const totalRows = Math.min(data.length, limit);
  const normalizedPage = Math.min(page, Math.floor((Math.min(totalRowsCount || 0, limit) - 1) / pageSize));

  React.useEffect(() => setPage(0), [totalRows, pageSize, data, createHash(widget)]);

  return (
    <AvTable
      rows={pageData}
      rowLimit={pageSize}
      paginationProps={{
        onPageChange: setPage,
        totalRows,
        pageNum: normalizedPage,
      }}
      {...getSharedProps(props)}
    />
  );
};

const DefaultTable = (props: Props) => {
  const { crossTableView, totalRowCount, totalRowCountDrillDown, widget, data, setOffset } = props;
  const rowLimit = widget.definition.custom.pageSize || widget.requests.at(0)?.top?.size || 25;
  return (
    <AvTable
      rows={data}
      rowLimit={rowLimit || widget.definition.custom.pageSize}
      paginationProps={{
        onPageChange: page => setOffset(page * rowLimit),
        totalRows: crossTableView ? totalRowCount : totalRowCountDrillDown || totalRowCount,
        pageNum: (widget.requests[0].top?.offset || 0) / rowLimit,
      }}
      {...getSharedProps(props)}
    />
  );
};

export default function Table(props: Props) {
  const { widget } = props;

  const isDrilldown = (widget.drillDownHierarchy?.activeIndex || 0) > 0;
  const isTopDefined = !!widget.definition.custom.topN;
  const isDefaultTable = Boolean(isDrilldown || !isTopDefined);

  const Component = isDefaultTable ? DefaultTable : LocalPaginationTable;

  return (
    <Box
      className="draggableCancel"
      sx={{
        ...flex.center,
        flex: 1,
        overflow: 'auto',
        p: 2,
        [`& .${tableHeadClasses.root} .${tableRowClasses.head} .${tableCellClasses.root}`]: {
          background: 'var(--color-surface-base-primary)',
        },
      }}>
      <Component {...props} />
    </Box>
  );
}

const getSharedProps = ({
  crossTableView,
  nextDrillDownField,
  widget,
  onSelect,
  onActiveCross,
  visibleHeadCells,
  responseTotals,
  onSort,
  isLoading,
  isLoadingDrillDown,
  showExpandedRow,
  expandedRow,
  commonTableProps,
  headCells,
  activeRow,
}: Props) =>
  ({
    ...commonTableProps,
    shouldDisplayNull: true,
    resizable: true,
    size: widget.definition.custom.rowSize,
    headCells,
    onActive: onActiveCross,
    onSort,
    activeKey: activeRow[0],
    pagination: !widget.requests[0].top?.groupOthers,
    loading: isLoading || (isLoadingDrillDown && !crossTableView),
    sx: {
      height: '100%',
      ...(((!crossTableView && !!nextDrillDownField && widget.requests[0].select.dims.length === 1) || onSelect) && {
        '.MuiTableRow-root.MuiTableRow-hover:hover': { backgroundColor: 'transparent' },
      }),
    },
    totalProps: widget.definition.custom.showTotals ? getTableTotalProps(visibleHeadCells, responseTotals) : {},
    ...(showExpandedRow && { expandedRowObj: { component: expandedRow } }),
  }) as const satisfies AvTableProps;
